
import React from "react";
import { Separator } from "@/components/ui/separator";
import { Check, X, FileCheck, User, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface PendingPrompt {
  id: string;
  title: string;
  description: string;
  content: string;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  user_id: string;
  author: {
    name: string;
    email: string;
  };
  submittedAt: string;
}

interface PendingPromptsTabProps {
  pendingPrompts: PendingPrompt[];
  isLoading: boolean;
  handleApprove: (id: string) => Promise<void>;
  handleReject: (id: string) => Promise<void>;
  handleDelete: (id: string) => Promise<void>;
}

const PendingPromptsTab = ({
  pendingPrompts,
  isLoading,
  handleApprove,
  handleReject,
  handleDelete,
}: PendingPromptsTabProps) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (pendingPrompts.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary/30 rounded-lg">
        <FileCheck className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">All prompts have been reviewed. No pending submissions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {pendingPrompts.map((prompt) => (
        <div 
          key={prompt.id} 
          className="bg-secondary/30 rounded-lg p-6 border border-border/40"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold">{prompt.title}</h3>
              <p className="text-sm text-muted-foreground">{prompt.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1 border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-red-900 dark:hover:bg-red-950"
                onClick={() => handleDelete(prompt.id)}
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1 border-red-200 hover:bg-red-50 hover:text-red-600 dark:border-red-900 dark:hover:bg-red-950"
                onClick={() => handleReject(prompt.id)}
              >
                <X className="h-4 w-4" />
                <span>Reject</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1 border-green-200 hover:bg-green-50 hover:text-green-600 dark:border-green-900 dark:hover:bg-green-950"
                onClick={() => handleApprove(prompt.id)}
              >
                <Check className="h-4 w-4" />
                <span>Approve</span>
              </Button>
            </div>
          </div>
          
          <Separator className="my-4" />
          
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Prompt Content:</h4>
            <div className="bg-background rounded p-4 text-sm font-mono whitespace-pre-wrap">
              {prompt.content}
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5" />
              <span>
                Submitted by {prompt.author.name} ({prompt.author.email})
              </span>
            </div>
            <div>
              <Badge variant="info" className="mr-2">Category: {prompt.category.name}</Badge>
              <span>
                Submitted on {new Date(prompt.submittedAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingPromptsTab;
